/*
 * Manmukh Ayurveda Enterprise Framework
 * File: assets/ma-card-collection.js
 *
 * PROGRESSIVE "VIEW MORE" FOR THE CARD ENGINE.
 *
 * Every card is already in the HTML when this runs. Nothing here fetches,
 * renders or reveals content — it only collapses what is already present, and
 * only once it has confirmed there is something worth collapsing. That order
 * matters and is the whole design:
 *
 *   - a crawler, and a reader whose JavaScript failed or was blocked, sees the
 *     complete list, because the server never marked anything hidden;
 *   - the button ships with the `hidden` attribute and is revealed here, so a
 *     control that does nothing can never appear;
 *   - if the list is already short enough, the button stays hidden.
 *
 * Accessibility: the button owns `aria-expanded` and points at the grid it
 * controls with `aria-controls`. Expanding moves focus to the first card that
 * was just revealed, so a keyboard or screen-reader user lands on the new
 * content instead of being left at the bottom of the old list. Collapsing
 * returns focus to the button, which is the element that caused the change.
 *
 * There is no height animation. Animating a grid's height is the part of this
 * pattern that fights reduced-motion preferences and causes layout thrash, and
 * it buys nothing a reader needs.
 */

class MaCardReveal extends HTMLElement {
  connectedCallback() {
    if (this.dataset.maReady === 'true') return;

    const grid = this.querySelector('[data-ma-cards]');
    const button = this.querySelector('[data-ma-cards-toggle]');
    if (!grid || !button) return;

    const items = Array.from(grid.querySelectorAll('.ma-grid__item'));
    const initial = parseInt(this.dataset.initial || '6', 10);

    // Nothing to collapse: leave every card visible and the button hidden.
    if (!Number.isFinite(initial) || initial < 1 || items.length <= initial) return;

    this.dataset.maReady = 'true';
    this.overflow = items.slice(initial);
    this.button = button;
    this.allowCollapse = this.dataset.allowCollapse !== 'false';
    this.expanded = false;

    this.collapse(false);
    button.hidden = false;
    button.addEventListener('click', this.onToggle);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onToggle);
  }

  onToggle = () => {
    if (this.expanded) {
      if (!this.allowCollapse) return;
      this.collapse(true);
    } else {
      this.expand();
    }
  };

  collapse(returnFocus) {
    this.overflow.forEach((item) => {
      item.hidden = true;
    });
    this.expanded = false;
    this.button.setAttribute('aria-expanded', 'false');
    this.button.textContent = this.button.dataset.labelMore || 'View more';
    this.button.hidden = false;
    if (returnFocus) this.button.focus();
  }

  expand() {
    this.overflow.forEach((item) => {
      item.hidden = false;
    });
    this.expanded = true;
    this.button.setAttribute('aria-expanded', 'true');

    if (this.allowCollapse) {
      this.button.textContent = this.button.dataset.labelLess || 'Show less';
    } else {
      // One-way reveal: the control has done its only job, so remove it rather
      // than leave a button that no longer changes anything.
      this.button.hidden = true;
    }

    const first = this.overflow[0];
    if (!first) return;
    const focusable = first.querySelector('a[href], button:not([disabled])');
    const target = focusable || first;
    if (!focusable) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }
}

if (!customElements.get('ma-card-reveal')) {
  customElements.define('ma-card-reveal', MaCardReveal);
}
